import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createGlobalStyle, ThemeProvider } from 'styled-components';

// Components
import { ConfigProvider } from 'antd';
import FullPageLoader from './components/FullPageLoader';
import OpportuneRoutes from './router/opportune';
import ElrinRoutes from './router';

// Constants
import {
  API_URL,
  APP_ENV,
  CHECK_DOMAIN,
  DEFAULT_ENVIRONMENT,
  HRMS_TYPE,
  LOGGED_OUT_ROUTES,
} from './constant';
import theme, { colorPalette } from './theme';

// Hooks
import { useSession } from './Providers/SessionProviders';

// fonts
import '@fontsource/barlow/300.css';
import '@fontsource/barlow/400.css';
import '@fontsource/barlow/500.css';
import '@fontsource/barlow/600.css';
// import '@fontsource/barlow/700.css';
// import '@fontsource/barlow/800.css';

// utils
import { translate } from './i18n/i18n';
import {
  generateLinkText,
  getAuthToken,
  openNotification,
  getOpportuneCompanyNameFromURL,
  getCompanySlugFromURL,
} from './utils';

const GlobalStyle = createGlobalStyle`
  a {
    color: ${(props) => props.theme.colorPrimary};
    text-decoration: none;
  }
`;

const t = translate(['pageTitle']);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyName = getOpportuneCompanyNameFromURL(window.location);
  const { company, setCompany, setCurrentUser } = useSession();
  const [showCompanyLoader, setShowCompanyLoader] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const index = [DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV) ? 2 : 1;
    document.title = t(`${location.pathname.split('/')[index] || 'dashboard'}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    // validateCompany();
    if (!CHECK_DOMAIN) {
      checkUser();
      setShowCompanyLoader(false);
    } else {
      validateCompany();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateCompany = async () => {
    setShowCompanyLoader(true);
    const hostname = getCompanySlugFromURL();
    try {
      const params = {};
      if ([DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV)) {
        params.hrms_type = HRMS_TYPE.OPPORTUNE;
      }
      const response = await axios.get(`${API_URL}/company/${hostname}/`, {
        params,
      });
      setCompany(response);
      checkUser();
      if (
        APP_ENV === DEFAULT_ENVIRONMENT.OPPORTUNE &&
        companyName != response?.slug
      ) {
        const path = window.location.pathname.replace(
          `${hostname}`,
          response?.company?.slug,
        );
        navigate(path, { replace: true });
      }
    } catch {
      navigate(generateLinkText('/not-found'));
      setIsLoading(false);
    } finally {
      setShowCompanyLoader(false);
    }
  };

  const checkUser = async () => {
    if (getAuthToken()) {
      fetchUserDetails();
    } else {
      setIsLoading(false);
      const index = [DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV) ? 2 : 1;
      const routeName = location.pathname.split('/')[index];
      if (!LOGGED_OUT_ROUTES.includes(routeName)) {
        navigate(generateLinkText('/login'));
      }
    }
  };

  const fetchUserDetails = async () => {
    setIsLoading(true);
    try {
      const params = { dashboard: 1, company: getCompanySlugFromURL() };
      const response = await axios.get(`${API_URL}/users/profile/`, {
        params,
      });
      setCurrentUser({ ...response });
      // setCurrentUser(response);
      const index = [DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV) ? 2 : 1;
      const routeName = location.pathname.split('/')[index];
      if (LOGGED_OUT_ROUTES.includes(routeName)) {
        navigate(generateLinkText('/'));
      }
    } catch (error) {
      const message = error.data?.message || error.message || null;
      // navigate('/not-found');
      if (message) {
        openNotification(error.message, null, 'error');
      }
      navigate(generateLinkText('/login'));
    } finally {
      setIsLoading(false);
    }
  };

  if (showCompanyLoader || isLoading) return <FullPageLoader />;

  return (
    <ConfigProvider
      theme={{
        ...theme,
        token: {
          ...theme.token,
          colorPrimary: company?.primary_color || colorPalette.primary[500],
        },
      }}>
      <ThemeProvider
        theme={{
          colorPrimary: company?.primary_color || colorPalette.primary[500],
        }}>
        <GlobalStyle />
        {[DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV) ? (
          <OpportuneRoutes />
        ) : (
          <ElrinRoutes />
        )}
      </ThemeProvider>
    </ConfigProvider>
  );
}

export default App;
